// src/admin/pages/Members.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { Card } from "../ui/Card";
import { Badge } from "../ui/Badge";

function normalize(s) {
  return String(s || "").toLowerCase().trim();
}

function overlaps(aStart, aEnd, bStart, bEnd) {
  // inclusive overlap on dates (YYYY-MM-DD)
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  const aS = new Date(aStart).getTime();
  const aE = new Date(aEnd).getTime();
  const bS = new Date(bStart).getTime();
  const bE = new Date(bEnd).getTime();
  return aS <= bE && bS <= aE;
}

function daysBetween(startISO, endISO) {
  if (!startISO || !endISO) return null;
  const a = new Date(startISO);
  const b = new Date(endISO);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const ms = b.getTime() - a.getTime();
  const raw = Math.round(ms / (1000 * 60 * 60 * 24));
  return raw === 0 ? 1 : raw;
}

function fullNameFromContact(c) {
  const first = c?.first_name || "";
  const last = c?.last_name || "";
  return `${first} ${last}`.trim() || "Unnamed";
}

function riskForMemberRow(row) {
  // row is { membership, contact }
  if (!row) return { key: "neutral", label: "—" };
  const m = row.membership;
  if (!m?.ageVerified) return { key: "danger", label: "🔴 HIGH (Age not verified)" };
  if (m?.status !== "active") return { key: "warning", label: "🟡 MED (Not active)" };
  if ((m?.daysRemaining ?? 0) <= 2) return { key: "warning", label: "🟡 MED (Low days)" };
  return { key: "success", label: "🟢 LOW" };
}

function KV({ k, v }) {
  return (
    <div className="memKV">
      <span>{k}</span>
      <div className="memKV__v">{v}</div>
    </div>
  );
}

export default function Members() {
  const navigate = useNavigate();

  // Search bar state
  const [query, setQuery] = useState("");

  // Selected (membership id) state
  const [selectedId, setSelectedId] = useState("");

  // Supabase data
  const [rows, setRows] = useState([]); // [{membership, contact}]
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]); // for conflict checks
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);

  // Availability inputs
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [checkIn, setCheckIn] = useState("");

  // ---------- Load data ----------
  useEffect(() => {
    let cancelled = false;

    async function loadAll() {
      setLoading(true);
      setLoadErr(null);

      try {
        // MEMBERS (membership + contact)
        const { data: memData, error: memErr } = await supabase
          .from("memberships")
          .select(
            `
            id,
            created_at,
            status,
            tier,
            billing,
            days_remaining,
            can_book,
            age_verified,
            start_date,
            renewal_date,
            contact_id,
            crm_contacts:contact_id (
              id,
              first_name,
              last_name,
              email,
              phone,
              stage,
              source,
              created_at,
              notes
            )
          `
          )
          .order("created_at", { ascending: false });

        if (memErr) throw memErr;

        // VEHICLES
        const { data: vehicleData, error: vehErr } = await supabase
          .from("vehicles")
          .select("id, name, category, status, location, tier_access, created_at")
          .order("created_at", { ascending: false });

        if (vehErr) throw vehErr;

        // BOOKINGS (for conflict checks)
        const { data: bookingData, error: bookErr } = await supabase
          .from("bookings")
          .select("id, vehicle_id, profile_id, start_date, end_date, status, created_at")
          .in("status", ["approved", "active"])
          .order("created_at", { ascending: false })
          .limit(500);

        if (bookErr) throw bookErr;

        if (!cancelled) {
          const mapped =
            (memData || []).map((m) => ({
              membership: {
                id: m.id,
                status: m.status,
                tier: m.tier,
                billing: m.billing,
                daysRemaining: m.days_remaining,
                canBook: m.can_book,
                ageVerified: m.age_verified,
                startDate: m.start_date,
                renewalDate: m.renewal_date,
              },
              contact: m.crm_contacts || null,
            })) || [];

          setRows(mapped);
          setVehicles(vehicleData || []);
          setBookings(bookingData || []);
        }
      } catch (e) {
        if (!cancelled) setLoadErr(e.message || "Failed to load data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadAll();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- Derived ----------
  const selectedRow = useMemo(() => {
    if (!selectedId) return null;
    return rows.find((r) => r.membership.id === selectedId) || null;
  }, [rows, selectedId]);

  const risk = useMemo(() => riskForMemberRow(selectedRow), [selectedRow]);

  // Search results only when typing
  const filteredRows = useMemo(() => {
    const q = normalize(query);
    if (!q) return [];

    return rows
      .filter((r) => {
        const c = r.contact || {};
        const m = r.membership || {};
        const hay = normalize(
          `${c.first_name || ""} ${c.last_name || ""} ${c.email || ""} ${c.phone || ""} ${
            c.id || ""
          } ${m.tier || ""} ${m.status || ""} ${m.billing || ""}`
        );
        return hay.includes(q);
      })
      .slice(0, 25);
  }, [rows, query]);

  // Eligible vehicles for selected tier
  const eligibleVehicles = useMemo(() => {
    if (!selectedRow) return [];
    const tier = selectedRow.membership.tier;
    return vehicles.filter((v) => Array.isArray(v.tier_access) && v.tier_access.includes(tier));
  }, [selectedRow, vehicles]);

  const eligibleVehiclesAvailable = useMemo(() => {
    return eligibleVehicles.filter((v) => v.status === "available");
  }, [eligibleVehicles]);

  // Pick default vehicle when selected member changes
  useEffect(() => {
    const next = eligibleVehiclesAvailable?.[0]?.id || eligibleVehicles?.[0]?.id || "";
    setSelectedVehicleId(next);
    setCheckOut("");
    setCheckIn("");
  }, [selectedId, eligibleVehiclesAvailable, eligibleVehicles]);

  const selectedVehicle = useMemo(() => {
    return selectedVehicleId ? vehicles.find((v) => v.id === selectedVehicleId) || null : null;
  }, [vehicles, selectedVehicleId]);

  const daysUsed = useMemo(() => {
    // Placeholder for when you join bookings by profile_id later
    return "—";
  }, []);

  const availabilityStatus = useMemo(() => {
    if (!selectedRow) return { ok: null, text: "Select a member first", detail: "" };
    if (!selectedVehicleId || !checkOut || !checkIn) return { ok: null, text: "Enter dates ↑", detail: "" };

    const v = vehicles.find((x) => x.id === selectedVehicleId);
    if (!v) return { ok: false, text: "Vehicle not found", detail: "" };

    if (v.status !== "available") {
      return { ok: false, text: "Not available", detail: `Ops status: ${v.status}` };
    }

    const conflict = bookings
      .filter((b) => b.vehicle_id === selectedVehicleId)
      .some((b) => overlaps(checkOut, checkIn, b.start_date, b.end_date));

    return conflict
      ? { ok: false, text: "❌ CONFLICT", detail: "Overlaps an approved/active booking." }
      : { ok: true, text: "✓ AVAILABLE", detail: "No overlap with approved/active bookings." };
  }, [selectedRow, selectedVehicleId, checkOut, checkIn, vehicles, bookings]);

  // ---------- Render ----------
  return (
    <div className="page">
      <div className="page__head page__head--row">
        <div>
          <h1 className="page__title">Members</h1>
          <div className="page__sub">
            Concierge view: search member → select → see profile + check availability.
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Link className="btn btn--primary" to="/admin/members/new">
            + Add Member
          </Link>
          <Link className="btn btn--primary" to="/admin/bookings/new">
            + Book Call-In
          </Link>
        </div>
      </div>

      {loading && <div className="memMuted">Loading members from Supabase…</div>}
      {loadErr && (
        <div className="adminAuth__error" style={{ marginTop: 10 }}>
          {loadErr}
        </div>
      )}

      <div className="memGrid">
        {/* LEFT */}
        <div className="memCol">
          <Card
            title="Customer Quick Lookup"
            subtitle="Search by name, email, phone"
            right={
              <div className="memTopRight">
                <div className="memRisk">
                  <div className="memLabel">Risk</div>
                  <Badge tone={risk.key}>{risk.label}</Badge>
                </div>
              </div>
            }
          >
            <div className="memLabel">Search</div>
            <input
              className="memInput"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Start typing… (ex: name, email, phone)"
              disabled={loading}
            />

            {!query && (
              <div className="memMuted">
                Start typing to search members by name, email, or phone.
              </div>
            )}

            {/* ✅ Results list only appears when typing */}
            {!!query && (
              <div className="memResults">
                {filteredRows.map((r) => {
                  const c = r.contact || {};
                  const m = r.membership || {};
                  const name = fullNameFromContact(c);

                  return (
                    <button
                      key={m.id}
                      type="button"
                      className={`memResult ${m.id === selectedId ? "memResult--active" : ""}`}
                      onClick={() => {
                        // ✅ If they click a result, go straight to details AND hide the list
                        setSelectedId(m.id);
                        setQuery(""); // hides the list immediately
                        navigate(`/admin/members/${m.id}`);
                      }}
                    >
                      <div className="memResult__main">
                        <div className="memResult__name">
                          {name} <span className="memResult__meta">• {c.email || "—"}</span>
                        </div>
                        <div className="memResult__sub">
                          {c.phone || "—"} • Tier: {m.tier} • Status: {m.status}
                        </div>
                      </div>

                      <div className="memResult__right">
                        <Badge tone="info">{m.tier}</Badge>
                        <Badge tone={m.status === "active" ? "success" : "warning"}>
                          {m.status}
                        </Badge>

                        {/* ✅ Explicit details link (optional, but nice UX) */}
                        <Link
                          className="btn btn--ghost"
                          to={`/admin/members/${m.id}`}
                          onClick={(e) => e.stopPropagation()} // don't double-trigger button click
                        >
                          View
                        </Link>
                      </div>
                    </button>
                  );
                })}

                {!filteredRows.length && (
                  <div className="memMuted">
                    No matches. Try email or phone.
                  </div>
                )}
              </div>
            )}

            {/* ✅ Profile only shows after selecting a member */}
            {!selectedRow ? (
              <div className="memMuted" style={{ marginTop: 14 }}>
                Select a member to view profile details.
              </div>
            ) : (
              (() => {
                const c = selectedRow.contact || {};
                const m = selectedRow.membership || {};
                const name = fullNameFromContact(c);

                return (
                  <div className="memLookupGrid" style={{ marginTop: 14 }}>
                    <div className="memInfoBlock">
                      <div className="memBlockTitle">Customer Info</div>
                      <div className="memCustomerName">{name}</div>
                      <KV k="Email" v={c.email || "—"} />
                      <KV k="Phone" v={c.phone || "—"} />
                      <KV k="CRM Stage" v={<Badge tone="neutral">{c.stage || "—"}</Badge>} />
                    </div>

                    <div className="memInfoBlock">
                      <div className="memBlockTitle">Membership</div>
                      <KV k="Tier" v={<Badge tone="info">{m.tier}</Badge>} />
                      <KV k="Billing" v={m.billing || "—"} />
                      <KV
                        k="Status"
                        v={
                          <Badge
                            tone={
                              m.status === "active"
                                ? "success"
                                : m.status === "prospect"
                                ? "warning"
                                : "neutral"
                            }
                          >
                            {m.status}
                          </Badge>
                        }
                      />
                      <KV
                        k="Age Verified"
                        v={m.ageVerified ? <Badge tone="success">Yes</Badge> : <Badge tone="danger">No</Badge>}
                      />
                      <KV
                        k="Can book"
                        v={m.canBook ? <Badge tone="success">Yes</Badge> : <Badge tone="warning">No</Badge>}
                      />
                    </div>

                    <div className="memInfoBlock">
                      <div className="memBlockTitle">Days</div>
                      <KV k="Days Used" v={daysUsed} />
                      <KV k="Days Remaining" v={m.daysRemaining ?? "—"} />
                    </div>

                    <div className="memInfoBlock">
                      <div className="memBlockTitle">Notes</div>
                      {Array.isArray(c.notes) && c.notes.length ? (
                        <div className="memNotes">
                          {c.notes.slice(0, 4).map((n, idx) => (
                            <div key={idx} className="memNote">
                              <div className="memNote__at">{n.at || "—"}</div>
                              <div className="memNote__text">{n.text || ""}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="muted">No notes yet.</div>
                      )}
                    </div>

                    {/* ✅ Now there IS a details link, and it works */}
                    <div className="memInfoBlock" style={{ gridColumn: "1 / -1" }}>
                      <div className="memBlockTitle">Open member record</div>
                      <div className="memMuted">
                        Go to full page to see bookings, timeline, and create requests.
                      </div>
                      <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <Link className="btn btn--primary" to={`/admin/members/${m.id}`}>
                          View Member Detail →
                        </Link>
                        <Link className="btn btn--ghost" to={`/admin/bookings/new?memberId=${m.id}`}>
                          + Create Booking
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })()
            )}
          </Card>
        </div>

        {/* RIGHT */}
        <div className="memCol">
          <Card title="Availability / Date Check" subtitle="Pick eligible vehicle + dates">
            {!selectedRow ? (
              <div className="memMuted">
                Select a member first to check eligibility and vehicle availability.
              </div>
            ) : (
              <>
                <div className="memFormRow">
                  <div>
                    <div className="memLabel">Vehicle (eligible for this tier)</div>
                    <select
                      className="memSelect"
                      value={selectedVehicleId}
                      onChange={(e) => setSelectedVehicleId(e.target.value)}
                    >
                      {(eligibleVehiclesAvailable.length ? eligibleVehiclesAvailable : eligibleVehicles).map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name} — {v.category} ({v.status})
                        </option>
                      ))}
                      {!eligibleVehicles.length && <option value="">No vehicles match this tier</option>}
                    </select>

                    {selectedVehicle && (
                      <div className="memVehicleMeta">
                        <Badge tone="info">{selectedVehicle.category}</Badge>
                        <span className="muted">{selectedVehicle.location || "—"}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="memFormRow memFormRow--2">
                  <div>
                    <div className="memLabel">Check-Out</div>
                    <input
                      className="memInput"
                      type="date"
                      value={checkOut}
                      onChange={(e) => setCheckOut(e.target.value)}
                    />
                  </div>
                  <div>
                    <div className="memLabel">Check-In</div>
                    <input
                      className="memInput"
                      type="date"
                      value={checkIn}
                      onChange={(e) => setCheckIn(e.target.value)}
                    />
                  </div>
                </div>

                <div className="memStatusRow">
                  <div className="memStatus">
                    <div className="memLabel">Days</div>
                    <div className="memBig">
                      {checkOut && checkIn ? (daysBetween(checkOut, checkIn) ?? "—") : "—"}
                    </div>
                  </div>

                  <div className="memStatus">
                    <div className="memLabel">Availability</div>
                    <div>
                      {availabilityStatus.ok === null ? (
                        <Badge tone="neutral">{availabilityStatus.text}</Badge>
                      ) : availabilityStatus.ok ? (
                        <Badge tone="success">{availabilityStatus.text}</Badge>
                      ) : (
                        <Badge tone="danger">{availabilityStatus.text}</Badge>
                      )}
                      {availabilityStatus.detail && <div className="memMuted">{availabilityStatus.detail}</div>}
                    </div>
                  </div>
                </div>

                <div className="memMuted">
                  v1 logic: checks overlap against <b>approved/active</b> bookings for the selected vehicle.
                  Ops status must be <b>available</b>.
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}